// src/sim/collision.ts — Kapsül–kapsül konumsal ayırma (ARCHITECTURE §4.1), AABB duvar + kenar freni (§4.2/§4.3), kill plane.
// Push yer değiştirmeye eklenir (hıza değil — FT dersi); kök hareket ve keepMomentum dâhil toplam dx/dz aynı frenden geçer.
// three'siz; tahsissiz: double'lar fonksiyonlar arası `R` yazmacıyla (arena.ts) ve modül Float64Array'leriyle geçer
// (V8: inline edilmeyen çağrıda double argüman/dönüş HeapNumber tahsis eder — determinism.test heap kabulü).
import { FState } from '../core/types.ts';
import type { Fighter, SimWorld } from '../core/types.ts';
import { hashNoise } from '../core/math.ts';
import { CAP_H, CAP_R, LYING_CAP_H, MAX_FIGHTERS, SEP_EPS, SEP_MAX, SEP_RATE } from '../config/constants.ts';
import { T } from '../config/tuning.ts';
import { Cap, capabilities } from './fighter.ts';
import { R, isQueryEx } from './arena.ts';

/** Kenar freni kipleri: yok / çubuk kuralı (< 0.60 durur) / her zaman kırp (Dodge, Roll). */
export const BRAKE_NONE = 0, BRAKE_STICK = 1, BRAKE_CLIP = 2;

/** Bu tick'in itişme yer değiştirmeleri (u): computePush yazar, stepLocomotion okur (tek iş parçacığı; aynı step içinde tüketilir). */
export const PUSH_X = new Float64Array(MAX_FIGHTERS);
export const PUSH_Z = new Float64Array(MAX_FIGHTERS);

const BRAKE_THRESHOLD = T.EDGE.brakeThreshold;
const CAPR = CAP_R;

/** Kapsül Y yüksekliği: yatan (Lying/Crawl/Roll/Knockdown) 0.9 — üstünden geçilir; diğerleri 2.2. */
export function capsuleHeight(f: Fighter): number {
  const s = f.state;
  return s === FState.Lying || s === FState.Crawl || s === FState.Roll || s === FState.Knockdown ? LYING_CAP_H : CAP_H;
}

/** İtişmeye katılım: caps Push ∧ doğuş koruması yok ∧ hitstop'ta değil. */
function participates(f: Fighter, tick: number): boolean {
  return (capabilities(f.state) & Cap.Push) !== 0 && f.spawnProtUntil <= tick && f.hitstop === 0;
}

/**
 * Tüm çiftler (n ≤ 8 → 28 çift, broadphase yok): o = 2·CAP_R − d; push = min(o·0.45, 0.06) u/tick, yarı yarıya.
 * Çıktı PUSH_X/PUSH_Z. d < 1e-4 → yön hashNoise (deterministik).
 */
export function computePush(world: SimWorld): void {
  const fs = world.fighters;
  const tick = world.tick;
  const n = fs.length < MAX_FIGHTERS ? fs.length : MAX_FIGHTERS;
  for (let i = 0; i < n; i++) { PUSH_X[i] = 0; PUSH_Z[i] = 0; }
  for (let i = 0; i < n; i++) {
    const a = fs[i]!;
    if (!participates(a, tick)) continue;
    const sa = a.state;
    const ha = sa === FState.Lying || sa === FState.Crawl || sa === FState.Roll || sa === FState.Knockdown ? LYING_CAP_H : CAP_H;
    for (let j = i + 1; j < n; j++) {
      const b = fs[j]!;
      if (!participates(b, tick)) continue;
      const dx = b.pos.x - a.pos.x, dz = b.pos.z - a.pos.z;
      const d = Math.sqrt(dx * dx + dz * dz);
      if (d >= 2 * CAPR) continue;
      const sb = b.state;
      const hb = sb === FState.Lying || sb === FState.Crawl || sb === FState.Roll || sb === FState.Knockdown ? LYING_CAP_H : CAP_H;
      if (!(a.pos.y < b.pos.y + hb && b.pos.y < a.pos.y + ha)) continue;   // Y aralıkları kesişmiyor
      let nx: number, nz: number;
      if (d < SEP_EPS) {
        const ang = hashNoise(world.seed, tick, i * MAX_FIGHTERS + j) * Math.PI * 2;
        nx = Math.cos(ang); nz = Math.sin(ang);
      } else { nx = dx / d; nz = dz / d; }
      const o = 2 * CAPR - d;
      let push = o * SEP_RATE; if (push > SEP_MAX) push = SEP_MAX;
      const h = push * 0.5;
      PUSH_X[i]! -= nx * h; PUSH_Z[i]! -= nz * h;
      PUSH_X[j]! += nx * h; PUSH_Z[j]! += nz * h;
    }
  }
  clipPushAtEdge(world, n);
}

/**
 * Ayrıştırma itmesi kimseyi platformdan atmaz: yerdeki dövüşçünün İTME yer değiştirmesinin dış bileşeni kenara kadar kırpılır.
 * Gerekçe (3. tur bulgusu): kenar freni (§4.3) yalnız {Idle, Move, Block, Carrying, Crawl, Attack} durumlarında çalışır; Lying
 * itişmeye katılır (Cap.Push) ama fren almaz → yerde yatan dövüşçü üstünden geçen rakiplerin itmesiyle SALDIRGANSIZ düşüyordu
 * ("Fell", vuruş olayı yok). Yalnız push bileşeni kırpılır: knockback/kayma (vel) ve çubuk kuralı dokunulmaz, ring-out hâlâ
 * bir vuruşun sonucudur. Havadaki dövüşçü kırpılmaz (zaten düşüyor). Tahsissiz (R yazmacı / NV).
 */
function clipPushAtEdge(world: SimWorld, n: number): void {
  const q = world.query;
  const fs = world.fighters;
  const qx = isQueryEx(q) ? q : null;
  for (let i = 0; i < n; i++) {
    let dx = PUSH_X[i]!, dz = PUSH_Z[i]!;
    if (dx === 0 && dz === 0) continue;
    const f = fs[i]!;
    if (!f.grounded) continue;
    const px = f.pos.x, pz = f.pos.z;
    if (qx) {
      R[0] = px + dx; R[1] = pz + dz;
      if (qx.isInsideR()) continue;
      // eksen-eksen kırpma (resolveHorizontal ile aynı kalıp; köşede iki eksen de kırpılır)
      if (dx !== 0) { R[0] = px; R[1] = pz; qx.extentAlongR(dx > 0 ? 1 : -1, 0); const ax = R[3]!; if ((dx > 0 ? dx : -dx) > ax) dx = dx > 0 ? ax : -ax; }
      if (dz !== 0) { R[0] = px + dx; R[1] = pz; qx.extentAlongR(0, dz > 0 ? 1 : -1); const az = R[3]!; if ((dz > 0 ? dz : -dz) > az) dz = dz > 0 ? az : -az; }
      R[0] = px + dx; R[1] = pz + dz;
      if (!qx.isInsideR()) { dx = 0; dz = 0; }   // float artığı
    } else {
      if (q.isInside(px + dx, pz + dz)) continue;
      const ed = q.edgeDist(px, pz, NV);
      const out = dx * NV.x + dz * NV.z;
      const allowed = ed > 0 ? ed : 0;
      if (out > allowed) { const e = out - allowed; dx -= NV.x * e; dz -= NV.z * e; }
      if (!q.isInside(px + dx, pz + dz)) { dx = 0; dz = 0; }
    }
    PUSH_X[i] = dx; PUSH_Z[i] = dz;
  }
}

/**
 * Yatay yer değiştirmeyi uygular. Girdi R yazmacından: R[8]=dx R[9]=dz R[10]=stickX R[11]=stickZ (fighter.ts yazar).
 * Kenar freni (yerde, kip'e göre) → X sonra Z eksen hareketi (duvar → geri al).
 * Kenar freni (DESIGN §3): next dışarıdaysa n = dış normal, stickOut = çubuk·n; stickOut < 0.60 → her eksen kenara kadar kırpılır
 * (karakter kenarda durur, teğet bileşen kalır, vel dış bileşeni 0); ≥ 0.60 geçer → düşer. BRAKE_CLIP her zaman kırpar.
 */
export function resolveHorizontal(world: SimWorld, f: Fighter, brake: number): void {
  const q = world.query;
  let dx = R[8]!, dz = R[9]!;
  if (dx === 0 && dz === 0) return;
  const stickX = R[10]!, stickZ = R[11]!;
  const px = f.pos.x, pz = f.pos.z;
  if (isQueryEx(q)) {
    if (brake !== BRAKE_NONE && f.grounded) {
      R[0] = px + dx; R[1] = pz + dz;
      if (!q.isInsideR()) {
        R[0] = px; R[1] = pz; q.edgeDistR();
        const ed = R[3]!, nx = R[4]!, nz = R[5]!;
        const stickOut = stickX * nx + stickZ * nz;
        if (brake === BRAKE_CLIP || stickOut < BRAKE_THRESHOLD) {
          if (ed >= 0) {
            // eksen-eksen kırpma: her eksende kenara kadar izin (köşede iki eksen de kırpılır, teğet bileşen kalır)
            if (dx !== 0) { R[0] = px; R[1] = pz; q.extentAlongR(dx > 0 ? 1 : -1, 0); const ax = R[3]!; if ((dx > 0 ? dx : -dx) > ax) { dx = dx > 0 ? ax : -ax; f.vel.x = 0; } }
            if (dz !== 0) { R[0] = px + dx; R[1] = pz; q.extentAlongR(0, dz > 0 ? 1 : -1); const az = R[3]!; if ((dz > 0 ? dz : -dz) > az) { dz = dz > 0 ? az : -az; f.vel.z = 0; } }
          }
          // float artığı: hâlâ dışarıdaysa eksenleri tek tek dene, olmazsa dur
          R[0] = px + dx; R[1] = pz + dz;
          if (!q.isInsideR()) {
            R[0] = px + dx; R[1] = pz;
            if (q.isInsideR()) { dz = 0; f.vel.z = 0; }
            else { R[0] = px; R[1] = pz + dz; if (q.isInsideR()) { dx = 0; f.vel.x = 0; } else { dx = 0; dz = 0; f.vel.x = 0; f.vel.z = 0; } }
          }
        }
      }
    }
    // eksen-eksen duvar testi (yüksek platform yanı; ilk arenada yok)
    R[2] = f.pos.y; R[6] = CAPR;
    if (dx !== 0) { R[0] = px + dx; R[1] = pz; if (q.wallHitR()) f.vel.x = 0; else f.pos.x = px + dx; }
    if (dz !== 0) { R[0] = f.pos.x; R[1] = pz + dz; if (q.wallHitR()) f.vel.z = 0; else f.pos.z = pz + dz; }
    return;
  }
  // yabancı ArenaQuery (arayüz yolu; tahsis olabilir): dış normal boyunca kırp
  if (brake !== BRAKE_NONE && f.grounded && !q.isInside(px + dx, pz + dz)) {
    const ed = q.edgeDist(px, pz, NV);
    const stickOut = stickX * NV.x + stickZ * NV.z;
    if (brake === BRAKE_CLIP || stickOut < BRAKE_THRESHOLD) {
      const out = dx * NV.x + dz * NV.z;
      const allowed = ed > 0 ? ed : 0;
      if (out > allowed) { const ex = out - allowed; dx -= NV.x * ex; dz -= NV.z * ex; }
      const vo = f.vel.x * NV.x + f.vel.z * NV.z;
      if (vo > 0) { f.vel.x -= NV.x * vo; f.vel.z -= NV.z * vo; }
      if (!q.isInside(px + dx, pz + dz)) {
        if (q.isInside(px + dx, pz)) { dz = 0; f.vel.z = 0; }
        else if (q.isInside(px, pz + dz)) { dx = 0; f.vel.x = 0; }
        else { dx = 0; dz = 0; f.vel.x = 0; f.vel.z = 0; }
      }
    }
  }
  if (dx !== 0) { if (q.blockedX(px + dx, pz, f.pos.y, CAPR)) f.vel.x = 0; else f.pos.x = px + dx; }
  if (dz !== 0) { if (q.blockedZ(f.pos.x, pz + dz, f.pos.y, CAPR)) f.vel.z = 0; else f.pos.z = pz + dz; }
}
const NV = { x: 0, y: 0, z: 0 };

/** Kill plane (DESIGN §8): y < −8. Falling/Eliminated zaten düşmüş; diğer her durum (Held/Carried dâhil — taşıyanla birlikte düşer). */
export const belowKillPlane = (world: SimWorld, f: Fighter): boolean =>
  f.pos.y < world.arena.killY && f.state !== FState.Falling && f.state !== FState.Eliminated;
